import { useState } from "react";

import { Alert, Button, Input, Modal, Typography } from "antd";

import { enviarPorCorreo } from "../../lib/api/client";
import type { CVDocument } from "../../lib/document/model";

const { Paragraph, Text } = Typography;

/**
 * Entregar la hoja de vida terminada.
 *
 * Llega por correo, no como descarga directa: `docs-microservices` genera el
 * PDF y el DOCX de forma asincrona (SQS -> S3 -> enlace firmado), asi que no
 * hay ningun archivo que devolver en el momento. Un boton "Descargar" que en
 * realidad manda un correo mentiria sobre lo que va a pasar, asi que el boton
 * dice lo que hace.
 */
export const EnviarModal = ({
  doc,
  abierto,
  onCerrar,
}: {
  doc: CVDocument;
  abierto: boolean;
  onCerrar: () => void;
}) => {
  const [correo, setCorreo] = useState(doc.email ?? "");
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valido = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correo.trim());

  const enviar = async () => {
    setEnviando(true);
    setError(null);
    try {
      await enviarPorCorreo(doc.id, correo.trim());
      setEnviado(true);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "No pudimos enviar tu hoja de vida.",
      );
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal
      open={abierto}
      title={enviado ? "Va en camino" : "Recibe tu hoja de vida"}
      onCancel={() => {
        onCerrar();
        setEnviado(false);
      }}
      footer={
        enviado
          ? [
              <Button key="ok" type="primary" onClick={onCerrar}>
                Listo
              </Button>,
            ]
          : [
              <Button key="c" onClick={onCerrar}>
                Cancelar
              </Button>,
              <Button
                key="e"
                type="primary"
                loading={enviando}
                disabled={!valido}
                onClick={() => void enviar()}
              >
                Enviármela
              </Button>,
            ]
      }
    >
      {enviado ? (
        <Paragraph>
          Te la mandamos a <Text strong>{correo}</Text> en PDF y Word. Puede
          tardar un par de minutos; si no llega, revisa tu carpeta de spam.
        </Paragraph>
      ) : (
        <>
          <Paragraph type="secondary">
            La generamos en PDF y Word y te la enviamos por correo.
          </Paragraph>
          {error && (
            <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />
          )}
          <Input
            type="email"
            size="large"
            placeholder="tucorreo@ejemplo.com"
            value={correo}
            onChange={(e) => setCorreo(e.target.value)}
            onPressEnter={() => valido && !enviando && void enviar()}
            autoFocus
          />
        </>
      )}
    </Modal>
  );
};
